/**
 * A list of decision right delegations for the home (migration 980, roadmap
 * D-4): each row names the right, who it came from or went to, and when it
 * ends — "Expired", "Expires in N days", or no end date. Expired delegations
 * stay listed and read as expired; nothing deletes them.
 *
 * Controls are drawn only for whoever may use them (build contract §2.8): the
 * database answers can_extend / can_revoke per row, from the same rules
 * extend_decision_right_delegation and revoke_delegation apply. The home shows
 * Extend and Revoke on the delegations a person made; on the ones made to them
 * it shows the term only.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endOfDayIso, suggestedEndDate, type MyDelegation } from '../../lib/decisionRightDelegations';
import { useDecisionRightsStore } from '../../stores/prioritization/decisionRightsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { btnDanger, btnPrimary, btnSecondary, field } from '@/components/mono/primitives';
import {
  delegationParty,
  delegationRightLabel,
  delegationTermLine,
  delegationTermTone,
} from './lib/delegations';

const TONE_COLOR: Record<'coral' | 'mustard' | 'quiet', string> = {
  coral: 'var(--coral-text)',
  mustard: 'var(--mustard)',
  quiet: 'var(--text-ter)',
};

export function DelegationList({ rows, onChanged }: { rows: MyDelegation[]; onChanged: () => void }) {
  return (
    <ul className="border-b border-[var(--rule)]" data-testid="delegation-list">
      {rows.map((d) => (
        <DelegationRow key={`${d.direction}:${d.id}`} d={d} onChanged={onChanged} />
      ))}
    </ul>
  );
}

function DelegationRow({ d, onChanged }: { d: MyDelegation; onChanged: () => void }) {
  const userId = useUserStore((s) => s.userId);
  const extendDelegation = useDecisionRightsStore((s) => s.extendDelegation);
  const revokeDelegation = useDecisionRightsStore((s) => s.revokeDelegation);

  const [extending, setExtending] = useState(false);
  const [date, setDate] = useState(() => suggestedEndDate(d.expiresAt));
  const [noEnd, setNoEnd] = useState(false);
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState<'extend' | 'revoke' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The delegator's controls. On a delegation made to this person the term is read, not changed here.
  const mine = d.direction === 'by_me';
  const showExtend = mine && d.canExtend;
  const showRevoke = mine && d.canRevoke;
  const lostAuthority = mine && !d.canExtend;

  const tone = delegationTermTone(d);
  const newEnd = noEnd ? null : endOfDayIso(date);
  const canSave = basis.trim().length > 0 && (noEnd || newEnd !== null) && busy === null;

  const save = async () => {
    if (!canSave) return;
    setBusy('extend');
    setError(null);
    const result = await extendDelegation({
      delegationId: d.id,
      expiresAt: newEnd ? new Date(newEnd) : null,
      basis: basis.trim(),
    });
    setBusy(null);
    if (!result.success) {
      setError(result.error ?? 'The end date could not be changed.');
      return;
    }
    setExtending(false);
    setBasis('');
    onChanged();
  };

  const revoke = async () => {
    if (!userId) return;
    setBusy('revoke');
    setError(null);
    const result = await revokeDelegation(d.id, userId);
    setBusy(null);
    if (!result.success) {
      setError(result.error ?? 'The delegation could not be revoked.');
      return;
    }
    onChanged();
  };

  return (
    <li className="border-t border-[var(--rule)] py-3" data-testid={`delegation-${d.direction}`}>
      <div className="flex items-start justify-between gap-4">
        <span className="min-w-0">
          <Link
            to={`/prioritization/cycles/${d.cycleId}`}
            className="block text-[15px] leading-snug text-[var(--text-pri)] hover:underline underline-offset-4"
          >
            {delegationRightLabel(d)}
          </Link>
          <span className="mt-0.5 block text-sm text-[var(--text-ter)]">{delegationParty(d)}</span>
        </span>
        <span
          className="shrink-0 text-right font-mono text-xs uppercase tracking-[0.08em]"
          style={{ color: TONE_COLOR[tone] }}
          data-testid="delegation-term"
        >
          {delegationTermLine(d)}
        </span>
      </div>

      {lostAuthority && (
        <p className="mt-1.5 text-sm text-[var(--text-ter)]">
          You no longer hold this right, so its holder or the cycle owner changes when it ends.
        </p>
      )}

      {(showExtend || showRevoke) && !extending && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {showExtend && (
            <button type="button" onClick={() => setExtending(true)} disabled={busy !== null} {...btnSecondary()}>
              Extend
            </button>
          )}
          {showRevoke && (
            <button type="button" onClick={() => void revoke()} disabled={busy !== null} {...btnDanger()}>
              {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
            </button>
          )}
        </div>
      )}

      {extending && (
        <div className="mt-3 flex flex-col gap-2.5" data-testid="delegation-extend-form">
          <label className="flex flex-col gap-1 text-sm text-[var(--text-sec)]">
            New end date
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={noEnd}
              {...field('max-w-[12rem]')}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <input type="checkbox" checked={noEnd} onChange={(e) => setNoEnd(e.target.checked)} />
            No end date — it stands until revoked
          </label>
          <label className="flex flex-col gap-1 text-sm text-[var(--text-sec)]">
            Why the end date changes
            <input
              type="text"
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
              placeholder="The review moved a week."
              {...field()}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void save()} disabled={!canSave} {...btnPrimary()}>
              {busy === 'extend' ? 'Saving…' : 'Save end date'}
            </button>
            <button
              type="button"
              onClick={() => {
                setExtending(false);
                setError(null);
              }}
              disabled={busy !== null}
              {...btnSecondary()}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--coral-text)' }}>
          {error}
        </p>
      )}
    </li>
  );
}
